/**
 * Admin Portal — S3 Backup Status panel.
 *
 * Shows a single source-of-truth health badge for the daily MongoDB→S3 backup:
 *   🟢 Green  — last successful backup < 26h ago
 *   🟡 Amber  — 26-48h, OR the most recent attempt failed
 *   🔴 Red    — > 48h, OR never succeeded, OR AWS env vars not configured
 *
 * Also surfaces:
 *   • Last 5 attempts (success or failure) — quick timeline
 *   • S3 storage size + soft warning if past 5 GB free-tier ceiling
 *   • Setup hint when AWS env vars aren't configured yet
 *
 * Data comes from GET /api/admin/backups/status (admin-auth).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, ShieldCheck, AlertTriangle, AlertCircle, RefreshCw, Cloud, HardDrive } from 'lucide-react';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HEALTH_CONFIG = {
  green: { label: 'Healthy', icon: ShieldCheck, color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  amber: { label: 'Attention', icon: AlertTriangle, color: 'bg-amber-100 text-amber-800 border-amber-300' },
  red:   { label: 'Action needed', icon: AlertCircle, color: 'bg-red-100 text-red-800 border-red-300' },
};

const formatBytes = (b) => {
  if (b == null) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const formatHours = (h) => {
  if (h == null) return 'never';
  if (h < 1) return `${Math.round(h * 60)} min ago`;
  if (h < 48) return `${h.toFixed(1)} h ago`;
  return `${Math.round(h / 24)} days ago`;
};

export default function BackupStatusPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/backups/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setStatus(await res.json());
      else logger.error('Backup status load failed:', res.status);
    } catch (e) { logger.error('Backup status error:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !status) {
    return <div className="text-center py-8 text-slate-400" data-testid="backup-status-loading"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading backup status…</div>;
  }
  if (!status) {
    return <div className="text-center py-8 text-slate-400">Could not load backup status. Try refresh.</div>;
  }

  const cfg = HEALTH_CONFIG[status.health] || HEALTH_CONFIG.red;
  const Icon = cfg.icon;

  return (
    <div className="space-y-4" data-testid="backup-status-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2 text-white"><Cloud className="w-5 h-5 text-emerald-400" /> S3 Backup Status</h3>
          <p className="text-sm text-slate-400 mt-0.5">Daily MongoDB → AWS S3 snapshot. Runs at 03:00 UTC.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="text-white border-slate-600 hover:bg-slate-700" disabled={loading} data-testid="backup-status-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Health badge + key facts */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-3">
          <Badge className={`${cfg.color} text-sm py-1`} data-testid={`backup-health-${status.health}`}>
            <Icon className="w-3.5 h-3.5 mr-1.5" /> {cfg.label}
          </Badge>
          <span className="text-sm text-slate-300">Last successful backup: <span className="font-semibold text-white">{formatHours(status.hours_since_last)}</span></span>
        </div>

        {!status.configured && (
          <div className="bg-amber-950/40 border border-amber-700 rounded-md p-3 text-sm text-amber-200" data-testid="backup-not-configured">
            <p className="font-semibold mb-1">AWS env vars not configured in this environment.</p>
            <p>Set <code className="bg-amber-900/40 px-1 rounded">AWS_ACCESS_KEY_ID</code>, <code className="bg-amber-900/40 px-1 rounded">AWS_SECRET_ACCESS_KEY</code>, <code className="bg-amber-900/40 px-1 rounded">AWS_REGION</code>, and <code className="bg-amber-900/40 px-1 rounded">S3_BUCKET_NAME</code> in the Emergent deployment console and redeploy. See <code>/app/BACKUPS.md</code> for the IAM policy.</p>
          </div>
        )}

        {status.last_attempt_error && (
          <div className="text-xs text-red-300 bg-red-950/40 border border-red-800 rounded-md p-2">
            <span className="font-semibold">Last attempt error:</span> {status.last_attempt_error}
          </div>
        )}

        {/* Storage line */}
        {status.storage && !status.storage.error && (
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <HardDrive className="w-4 h-4 text-slate-500" />
            <span>Storage: <span className="text-white font-medium">{formatBytes(status.storage.total_bytes)}</span> across <span className="text-white font-medium">{status.storage.total_count}</span> backup(s) in S3.</span>
            {status.storage.over_warn && (
              <Badge className="bg-amber-100 text-amber-800 border border-amber-300 ml-2">Past free-tier ceiling — apply lifecycle policy</Badge>
            )}
          </div>
        )}
      </div>

      {/* Recent attempts timeline */}
      {status.recent && status.recent.length > 0 && (
        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-white mb-3">Recent attempts</h4>
          <div className="space-y-2">
            {status.recent.map((r, i) => (
              <div key={`${r.timestamp}-${i}`} className="flex items-center justify-between text-xs border-b border-gray-700/50 last:border-0 pb-2 last:pb-0">
                <div className="flex items-center gap-2">
                  {r.ok
                    ? <span className="text-emerald-400">✓</span>
                    : <span className="text-red-400">✗</span>}
                  <span className="text-slate-300 font-mono">{new Date(r.timestamp).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-400">
                  {r.size_bytes != null && <span>{formatBytes(r.size_bytes)}</span>}
                  {r.duration_seconds != null && <span>{r.duration_seconds.toFixed(1)}s</span>}
                  {r.error && <span className="text-red-300 max-w-[260px] truncate" title={r.error}>{r.error}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
